/**
 * Build a synthetic ApprovalDetailResponse from an AttentionItem.
 *
 * The platform migrated from /v1/approvals/{id} (which returned a structured
 * ApprovalCard schema) to a unified /v1/attention list endpoint that carries
 * the raw payload inline in `AttentionItem.details`. The legacy detail
 * endpoint no longer exists.
 *
 * This module bridges the gap on the frontend: given an AttentionItem we
 * already have in memory from the list call, it builds a card response
 * compatible with the existing ApprovalCard component, using the new
 * `type_aware` renderer to display the unstructured details map.
 *
 * The resulting card has top-level sections grouped by semantic purpose
 * (Tool Arguments, Proposed Resolution, Workflow Context, …) so the
 * operator gets meaningful structure even though the backend isn't
 * authoring it. Within each section, the type_aware renderer inspects
 * field names + value shapes to pick appropriate display flavours.
 */

import type {
  ApprovalDetailResponse,
  ApprovalItem,
  ApprovalCardSection,
  AttentionItem,
  ToolButtonConfig,
} from './types'
import { getAppLabel } from './utils/appLabel'

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Build a synthetic detail response for a tool approval AttentionItem.
 * Returns null if the item isn't suitable for approval-card rendering
 * (caller should fall through to the appropriate review panel instead).
 */
export function buildSyntheticDetail(item: AttentionItem): ApprovalDetailResponse {
  const details = (item.details ?? {}) as Record<string, unknown>
  const toolName = extractToolName(item, details)
  const sections = buildSections(details, toolName, item)
  const approvalItem = buildApprovalItem(item, toolName)
  const buttonConfig = TOOL_BUTTON_CONFIGS[toolName] ?? undefined
  // Which app this approval acts on (derived from args.app_id). Surfaced as a
  // chip in the detail header; null for tools that don't target an app.
  const appLabel = getAppLabel(item)

  return {
    item: approvalItem,
    card: {
      tool_name: toolName,
      display: {
        name: humaniseToolName(toolName),
        icon: 'tool',
        color: '#f59e0b', // amber — matches existing tool-approval badge color
      },
      app_label: appLabel,
      sections,
      side_effects: [],
      quick_reject_reasons: [],
      default_quick_reject_reasons: DEFAULT_QUICK_REJECT_REASONS,
      supports_edit: false,
      editable_fields: [],
      button_config: buttonConfig,
    },
    registry_version: 'synthetic-1',
  }
}

// ---------------------------------------------------------------------------
// Per-tool button config
//
// Mirrors the `button_config` field in the Python `tool_ui.yaml` registry.
// Because the frontend synthesises card payloads locally (the old
// /v1/approvals/{id} endpoint no longer exists), these overrides must be
// declared here too so that `AttentionInspector` and `RejectDialog` receive
// the correct labels without a backend round-trip.
//
// Keep this in sync with tool_ui.yaml:button_config entries.
// ---------------------------------------------------------------------------

const TOOL_BUTTON_CONFIGS: Record<string, ToolButtonConfig> = {
  escalate_to_operator: {
    hide_approve: true,
    reject_label: 'Respond',
    reject_dialog_title: 'Respond to Agent',
    reject_dialog_description:
      'Your message will be sent back to the agent as guidance. The agent will use it to adapt its approach.',
    reject_submit_label: 'Send Response',
    suppress_label: 'Dismiss',
  },
}

// ---------------------------------------------------------------------------
// Default quick-reject reasons
//
// The backend doesn't return tool-specific reasons because the card schema
// endpoint doesn't exist. These generic reasons cover the common cases
// across all tools and are good enough for feedback collection.
// ---------------------------------------------------------------------------

const DEFAULT_QUICK_REJECT_REASONS = [
  { id: 'wrong_customer', label: 'Wrong customer' },
  { id: 'wrong_action', label: 'Wrong action' },
  { id: 'wrong_content', label: 'Wrong content / tone' },
  { id: 'should_escalate', label: 'Should escalate to human' },
  { id: 'tool_misuse', label: 'Tool misuse' },
  { id: 'duplicate', label: 'Duplicate / already handled' },
  { id: 'unsafe', label: 'Unsafe action' },
]

// ---------------------------------------------------------------------------
// Section builders
// ---------------------------------------------------------------------------

/**
 * Group the AttentionItem.details map into top-level approval-card sections.
 * Each section uses the new `type_aware` renderer.
 *
 * Section order is intentional: most actionable info first.
 *
 * `toolName` is threaded in for the tool-aware sections (Task 3702's
 * Reviewer Segmentation block is only emitted for `post_review_reply`,
 * for example). Sections that aren't tool-aware ignore it.
 */
function buildSections(
  details: Record<string, unknown>,
  toolName: string,
  item: AttentionItem,
): ApprovalCardSection[] {
  const sections: ApprovalCardSection[] = []

  // Track which top-level keys we've consumed so the catch-all section
  // doesn't duplicate them.
  const consumed = new Set<string>()

  // 0) Operator Decision — history items only (item.resolution is set).
  //
  // For resolved items the most important thing the operator needs to see
  // in the history view is *what they decided* and *what feedback they gave*.
  // This section surfaces that context before everything else so the card
  // reads: decision → what the agent proposed → supporting context.
  //
  // Feedback sources:
  //   - `details.rejection_feedback` — workflow-item rejection text, stored in
  //     `e.Error` by RejectExecution and lifted by executionToDetailsMap.
  //   - `details.reviewer_notes` — approval notes AND agent-item rejection text
  //     (RecordAgentResolution stores feedback in ReviewerNotes).
  //   We surface whichever is present (rejection_feedback takes priority so
  //   the label "Feedback" reads correctly for the most common rejection case).
  if (item.resolution) {
    const decisionData: Record<string, unknown> = {
      outcome: item.resolution,
    }
    if (item.resolved_by) decisionData.decided_by = item.resolved_by
    if (item.resolved_at) decisionData.decided_at = item.resolved_at

    // Rejection feedback text (workflow items store it in `rejection_feedback`,
    // agent items store it in `reviewer_notes`).
    const feedback =
      (typeof details.rejection_feedback === 'string' && details.rejection_feedback.trim().length > 0)
        ? details.rejection_feedback.trim()
        : (typeof details.reviewer_notes === 'string' && details.reviewer_notes.trim().length > 0)
        ? details.reviewer_notes.trim()
        : null
    if (feedback) decisionData.feedback = feedback

    // Structured rejection reason IDs selected via the quick-reject UI.
    if (Array.isArray(details.rejection_quick_reasons) && details.rejection_quick_reasons.length > 0) {
      decisionData.quick_reasons = details.rejection_quick_reasons
    }

    const label =
      item.resolution === 'approved' ? 'Approval'
      : item.resolution === 'rejected' ? 'Rejection'
      : 'Operator Decision'

    sections.push({
      id: 'operator_decision',
      label,
      renderer: 'type_aware',
      collapsed: false,
      data: decisionData,
      error: null,
    })
    consumed.add('rejection_feedback')
    consumed.add('reviewer_notes')
    consumed.add('rejection_quick_reasons')
  }

  // 1) Execution summary — high-level "what happened" narrative.
  //
  // This is the FIRST thing operators want to read when reviewing an
  // approval ("what did the agent do, and why is this the right next
  // step?"), so it sits at the top of the card and is expanded by
  // default. Uses the bespoke `execution_summary` renderer for proper
  // prose / bullet / customer-block formatting rather than the
  // generic key/value flattening from `type_aware`.
  //
  // We always emit the section — even when no summary has been generated
  // yet — so the renderer can surface a "Summarise" button that lets the
  // operator request one on demand.
  //
  // Note: we emit `data: {}` (an empty object) rather than `data: null`
  // for the empty case. ApprovalCardSection short-circuits a `null`/
  // `undefined` data payload with a generic "No data provided." message
  // before the renderer is invoked (see ApprovalCardSection.tsx), so
  // returning null would suppress the CTA. The renderer detects "no
  // recognised fields" and shows the empty state with the button
  // (provided the surrounding execution context exposes an execution id).
  //
  // Collapsed by default when no summary exists yet — the empty-state CTA
  // ("No summary yet. Click Summarise…") is useful but not the first thing
  // the operator needs. Collapsing it when empty keeps the actionable
  // content (Tool Arguments, Original Review) in the viewport on open.
  const summaryData = isPlainObject(details.execution_summary)
    ? (details.execution_summary as Record<string, unknown>)
    : null
  const summaryIsEmpty = summaryData === null || Object.keys(summaryData).length === 0
  sections.push({
    id: 'execution_summary',
    label: 'Execution Summary',
    renderer: 'execution_summary',
    collapsed: summaryIsEmpty,
    data: summaryData ?? {},
    error: null,
  })
  consumed.add('execution_summary')

  // 1b) Original Review — for post_review_reply, surface the review the agent
  // is responding to (rating + text). Positioned BEFORE the proposed response
  // so the operator reads context → action in natural order. Expanded by
  // default — this is primary content for the approval decision, not
  // secondary metadata.
  //
  // Data source: under the canonical attention-item framework (Task 4065a)
  // the review rating and body travel in the tool-call `args`
  // (`review_rating`, `review_body`) — the same payload that carries the
  // proposed `response_text`. Legacy / workflow-sourced items instead carried
  // them on `trigger_payload.{plain_text_body, review.{rating, territory}}`,
  // so we read args first and fall back to the legacy shape for back-compat.
  if (toolName === 'post_review_reply') {
    const args = isPlainObject(details.args) ? (details.args as Record<string, unknown>) : null
    const tp = isPlainObject(details.trigger_payload)
      ? (details.trigger_payload as Record<string, unknown>)
      : null
    const legacyReview =
      tp !== null && isPlainObject(tp.review) ? (tp.review as Record<string, unknown>) : null

    const legacyCaseNotes =
      tp !== null && isPlainObject(tp.case_notes) ? (tp.case_notes as Record<string, unknown>) : null

    // Review text: prefer args.review_body, fall back to legacy plain_text_body.
    const reviewText = firstNonEmptyString(args?.review_body, tp?.plain_text_body)
    // Rating: prefer args.review_rating, fall back to legacy review.rating.
    const rating = firstNumber(args?.review_rating, legacyReview?.rating)
    // Store region / territory (Task 4074c). Historically this only lived on
    // the legacy `trigger_payload.review.territory`. We now also accept it from
    // the canonical tool-call args (`review_territory` / `territory`) so that
    // once the agent forwards the ASC territory it surfaces on the card without
    // a further frontend change. Canonical args take precedence over legacy.
    const territory = firstNonEmptyString(
      args?.review_territory,
      args?.territory,
      legacyReview?.territory,
    )
    // Reviewer name: prefer the canonical args.reviewer_nickname, fall back to
    // the legacy locations it lived in (review object / case_notes / sender).
    const reviewerName = firstNonEmptyString(
      args?.reviewer_nickname,
      legacyReview?.reviewer_nickname,
      legacyCaseNotes?.reviewer_nickname,
      tp?.from,
    )

    // Render whenever we have any review content. The rating alone is worth
    // surfacing even when the body is unavailable (e.g. older items, or a
    // call that omitted review_body).
    if (reviewText !== null || rating !== null) {
      const reviewData: Record<string, unknown> = {}
      if (reviewerName !== null) reviewData.reviewer = reviewerName
      if (rating !== null) {
        const filled = Math.max(0, Math.min(5, Math.round(rating)))
        reviewData.rating = `${'★'.repeat(filled)}${'☆'.repeat(5 - filled)} (${rating}/5)`
      }
      if (territory !== null) reviewData.territory = territory
      if (reviewText !== null) reviewData.review_text = reviewText

      sections.push({
        id: 'original_review',
        label: 'Original Review',
        renderer: 'type_aware',
        collapsed: false,
        data: reviewData,
        error: null,
      })
    }
  }

  // 1c) Original Email — for send_email, surface the customer's original
  // inbound email the agent is replying to (Task 4054d). Positioned BEFORE
  // the proposed reply (args) so the operator reads context → action.
  // Expanded by default — primary content for the approval decision.
  //
  // Two data sources, mirroring the post_review_reply split:
  //
  //   - Workflow-sourced (legacy) approvals carry the inbound Interaction
  //     inline in `details.trigger_payload`. We lift it synchronously into an
  //     `original_email` section. `trigger_payload` is intentionally NOT marked
  //     consumed — it still lands (in full) in the collapsed Workflow Context
  //     section below so the raw payload stays available for debugging.
  //
  //   - Agent-sourced approvals under the canonical attention-item framework
  //     (Task 4065a) carry NO trigger_payload — the subject is only
  //     {tool_name, args}. The inbound email is referenced by
  //     `args.interaction_id`, so we emit the section with the
  //     `interaction_email` renderer, which hydrates the email from the inbox
  //     API at render time (full fidelity: HTML body + attachments). Brand-new
  //     emails (no interaction_id) have no inbound message → section omitted.
  if (toolName === 'send_email') {
    const args = isPlainObject(details.args) ? (details.args as Record<string, unknown>) : null

    if (isPlainObject(details.trigger_payload)) {
      const emailData = extractOriginalEmail(details.trigger_payload as Record<string, unknown>)
      if (emailData) {
        sections.push({
          id: 'original_email',
          label: 'Original Email',
          renderer: 'original_email',
          collapsed: false,
          data: emailData,
          error: null,
        })
      }
    } else {
      const interactionId = firstNonEmptyString(args?.interaction_id)
      if (interactionId !== null) {
        sections.push({
          id: 'original_email',
          label: 'Original Email',
          renderer: 'interaction_email',
          collapsed: false,
          data: { interaction_id: interactionId },
          error: null,
        })
      }
    }
  }

  // 2) Tool arguments / Proposed Response.
  //
  // For most tools we surface the entire args payload as "Tool Arguments"
  // expanded by default.
  //
  // For `send_email`, the args ARE the proposed email (recipient, subject,
  // body). We surface them as "Proposed Resolution" (expanded) so operators
  // see the email content front-and-centre rather than a raw "Tool Arguments"
  // dump. For automation-sourced approvals `proposed_resolution` is already
  // set to the same payload, so we prefer it as the displayed data and mark it
  // consumed so the generic handler below doesn't double-emit it. The full
  // `args` payload is still available collapsed as "Tool Arguments" for
  // verification — mirrors the `post_review_reply` split.
  //
  // For `post_review_reply` we split: only `response_text` is shown
  // (re-labelled "Proposed Response") expanded by default. Everything
  // else in `args` (app_id, classification_signals, response_language,
  // review_id, review_rating) is metadata the operator doesn't need to
  // read to make the approve/reject call — it's parked in a collapsed
  // "Tool Arguments" section so it's still inspectable for debugging
  // without competing for visual attention with the review and the reply.
  if (isPlainObject(details.args)) {
    const args = details.args as Record<string, unknown>

    if (toolName === 'send_email') {
      // Prefer proposed_resolution when present (automation-sourced items set
      // it to the same email payload as args; using it keeps the section id
      // consistent with the section priority ordering in ApprovalCard.tsx).
      const proposedData = isPlainObject(details.proposed_resolution)
        ? (details.proposed_resolution as Record<string, unknown>)
        : args

      sections.push({
        id: 'proposed_resolution',
        label: 'Proposed Resolution',
        renderer: 'type_aware',
        collapsed: false,
        data: proposedData,
        error: null,
      })
      consumed.add('proposed_resolution')
      consumed.add('proposed_resolution_type')

      sections.push({
        id: 'args',
        label: 'Tool Arguments',
        renderer: 'type_aware',
        collapsed: true,
        data: args,
        error: null,
      })
    } else if (toolName === 'post_review_reply') {
      const responseText =
        typeof args.response_text === 'string' && args.response_text.trim().length > 0
          ? args.response_text.trim()
          : null

      if (responseText) {
        sections.push({
          id: 'proposed_response',
          label: 'Proposed Response',
          renderer: 'type_aware',
          collapsed: false,
          data: { response_text: responseText },
          error: null,
        })
      }

      // `response_text` is surfaced as "Proposed Response" above; `review_body`
      // is surfaced (in full) in the "Original Review" section above. Skip both
      // here so the collapsed Tool Arguments dump stays a compact metadata view
      // (app_id, review_id, review_rating, …) rather than repeating the two
      // long text blobs the operator already sees expanded.
      // Skip the long text blobs already surfaced expanded (response_text →
      // Proposed Response, review_body → Original Review) and the territory
      // (surfaced as the region chip in Original Review — Task 4074c) so the
      // collapsed Tool Arguments stays a compact metadata view without
      // duplicating fields the operator already sees.
      const otherArgs: Record<string, unknown> = {}
      for (const [k, v] of Object.entries(args)) {
        if (k === 'response_text' || k === 'review_body') continue
        if (k === 'review_territory' || k === 'territory') continue
        if (v === null || v === undefined || v === '') continue
        otherArgs[k] = v
      }
      if (Object.keys(otherArgs).length > 0) {
        sections.push({
          id: 'args',
          label: 'Tool Arguments',
          renderer: 'type_aware',
          collapsed: true,
          data: otherArgs,
          error: null,
        })
      }
    } else {
      sections.push({
        id: 'args',
        label: 'Tool Arguments',
        renderer: 'type_aware',
        collapsed: false,
        data: args,
        error: null,
      })
    }
    consumed.add('args')
    consumed.add('tool_name') // already shown in the header
  }

  // 2c) Pending message — the agent's natural-language explanation
  if (typeof details.pending_message === 'string' && details.pending_message.trim().length > 0) {
    sections.push({
      id: 'pending_message',
      label: 'Pending Message',
      renderer: 'type_aware',
      collapsed: false,
      data: { message: details.pending_message },
      error: null,
    })
    consumed.add('pending_message')
  }

  // 3) Proposed resolution — the workflow's recommended action
  if (isPlainObject(details.proposed_resolution)) {
    sections.push({
      id: 'proposed_resolution',
      label: 'Proposed Resolution',
      renderer: 'type_aware',
      collapsed: false,
      data: details.proposed_resolution,
      error: null,
    })
    consumed.add('proposed_resolution')
    consumed.add('proposed_resolution_type')
  }

  // 3b) Reviewer Segmentation — surfaces the support_manager's frustration
  // segment classification and the content_classifier's engagement signals
  // for `post_review_reply` approvals (Task 3702). The segment drives
  // `email_writer`'s private follow-up strategy (3701e); the public review
  // response is generic regardless of segment, but operators benefit from
  // seeing the segmentation upstream of the public-reply approval so they
  // can sanity-check the classifier and support_manager before the private
  // follow-up lands.
  //
  // Sources we attempt (most-structured first):
  //  1. `details.proposed_resolution.frustration_segment` — only present
  //     if the backend (support_manager / review_writer) explicitly lifts
  //     it onto the proposed-resolution payload. Not currently emitted;
  //     reserved for a future lift (see Post-Implementation Gaps).
  //  2. `details.frustration_segment` — top-level lift, also reserved.
  //  3. Engagement signals: `details.trigger_payload.classification.engagement_signals`
  //     (the persisted Interaction.Classification block written by the
  //     `step_persist_classification` step of the review automation; populated
  //     when the trigger payload is re-hydrated post-classification) and the
  //     `step_unpack_classifier` step output (always present once the
  //     classifier step has run). Either path is fine — we union them so
  //     we surface whatever the backend has.
  //
  // The section is collapsed by default — operators don't always need to
  // inspect it, but having it available means they can verify the
  // segmentation when a review feels misclassified before approving the
  // public response.
  if (toolName === 'post_review_reply') {
    const segment = extractFrustrationSegment(details)
    const engagement = extractEngagementSignals(details)
    if (segment || engagement) {
      const segmentationData: Record<string, unknown> = {}
      if (segment) {
        // `segment` is one of:
        //   wrong_fit | tenure_with_specific_problem |
        //   business_model_objection | unable_to_classify
        segmentationData.segment = segment
      }
      if (engagement) {
        if (typeof engagement.tenure_days === 'number') {
          segmentationData.tenure_days = engagement.tenure_days
        }
        if (typeof engagement.streak_length === 'number') {
          segmentationData.streak_length = engagement.streak_length
        }
        if (typeof engagement.commitment_language === 'boolean') {
          segmentationData.commitment_language = engagement.commitment_language
        }
        if (
          typeof engagement.verbatim_quote === 'string' &&
          engagement.verbatim_quote.trim().length > 0
        ) {
          segmentationData.engagement_quote = engagement.verbatim_quote
        }
      }

      if (Object.keys(segmentationData).length > 0) {
        sections.push({
          id: 'frustration_segment',
          label: 'Reviewer Segmentation',
          renderer: 'type_aware',
          collapsed: true,
          data: segmentationData,
          error: null,
        })
        // Mark the top-level lift paths as consumed so the catch-all
        // "Other Details" section doesn't duplicate them once the
        // backend writes `details.frustration_segment` or
        // `details.engagement_signals` directly (the reserved Path 2
        // lifts referenced by the extractors). Nested sources
        // (`proposed_resolution.*`, `trigger_payload.classification.*`,
        // `step_executions[*].output.*`) are not top-level keys so
        // adding them to `consumed` has no effect — but `consumed` is
        // a Set, so the no-op is harmless and the symmetry is easier to
        // reason about.
        consumed.add('frustration_segment')
        consumed.add('engagement_signals')
      }
    }
  }

  // 4) Workflow context — facets + trigger payload
  const workflowContext: Record<string, unknown> = {}
  if (isPlainObject(details.facets)) {
    workflowContext.facets = details.facets
    consumed.add('facets')
  }
  if (isPlainObject(details.trigger_payload)) {
    workflowContext.trigger_payload = details.trigger_payload
    consumed.add('trigger_payload')
  }
  if (typeof details.event_id === 'string' || typeof details.event_type === 'string') {
    if (details.event_id !== undefined) workflowContext.event_id = details.event_id
    if (details.event_type !== undefined) workflowContext.event_type = details.event_type
    consumed.add('event_id')
    consumed.add('event_type')
  }
  if (Object.keys(workflowContext).length > 0) {
    sections.push({
      id: 'workflow_context',
      label: 'Workflow Context',
      renderer: 'type_aware',
      collapsed: true,
      data: workflowContext,
      error: null,
    })
  }

  // 5) Step executions — past steps already executed in this workflow
  if (Array.isArray(details.step_executions) && details.step_executions.length > 0) {
    sections.push({
      id: 'step_executions',
      label: 'Step Executions',
      renderer: 'type_aware',
      collapsed: true,
      data: { steps: details.step_executions },
      error: null,
    })
    consumed.add('step_executions')
  }

  // (Execution summary is handled as section 1 above — it leads the card.)

  // 7) Catch-all: any details fields we haven't explicitly grouped above.
  //    Useful for new fields the backend adds without us having to know.
  const remainder: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(details)) {
    if (consumed.has(k)) continue
    // Skip empty / null fields to keep the section uncluttered
    if (v === null || v === undefined || v === '') continue
    remainder[k] = v
  }
  if (Object.keys(remainder).length > 0) {
    sections.push({
      id: 'other',
      label: 'Other Details',
      renderer: 'type_aware',
      collapsed: true,
      data: remainder,
      error: null,
    })
  }

  // Empty fallback: if nothing matched, show the whole details map raw
  if (sections.length === 0) {
    sections.push({
      id: 'raw',
      label: 'Details',
      renderer: 'type_aware',
      collapsed: false,
      data: Object.keys(details).length > 0 ? details : { value: '(no details available)' },
      error: null,
    })
  }

  return sections
}

// ---------------------------------------------------------------------------
// ApprovalItem shim
//
// The existing ApprovalCard component takes both the card and an
// ApprovalItem describing the bookkeeping (id, source, status, etc.).
// We synthesise a compatible item from the AttentionItem so the card
// renders without changes to the component.
// ---------------------------------------------------------------------------

function buildApprovalItem(item: AttentionItem, toolName: string): ApprovalItem {
  return {
    id: item.id,
    source: item.source === 'workflow' ? 'automation' : 'agent_tool',
    source_ref: item.execution_id ?? item.session_id ?? item.id,
    tool_name: toolName,
    agent_id: item.agent_id ?? '',
    display_name: humaniseToolName(toolName),
    icon: 'tool',
    color: '#f59e0b',
    created_at: item.created_at,
    trigger_summary: item.subtitle ?? '',
    breadcrumb: buildBreadcrumb(item),
    owner_id: null,
    blocking: false,
    status: item.resolution ? 'resolved' : 'pending',
    resolved_at: item.resolved_at ?? null,
    resolved_by: item.resolved_by ?? null,
    resolution: normaliseResolution(item.resolution),
  }
}

function buildBreadcrumb(item: AttentionItem): string {
  const parts: string[] = []
  if (item.agent_name) parts.push(item.agent_name)
  else if (item.agent_id) parts.push(item.agent_id)
  if (item.customer_name) parts.push(item.customer_name)
  else if (item.customer_email) parts.push(item.customer_email)
  return parts.join(' / ')
}

function normaliseResolution(
  res: string | null,
): ApprovalItem['resolution'] {
  if (res === 'approved' || res === 'rejected' || res === 'suppressed') return res
  return null
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function extractToolName(item: AttentionItem, details: Record<string, unknown>): string {
  // Direct on details
  if (typeof details.tool_name === 'string' && details.tool_name) return details.tool_name
  // Sometimes nested in proposed_resolution
  if (isPlainObject(details.proposed_resolution)) {
    const pr = details.proposed_resolution as Record<string, unknown>
    if (typeof pr.tool_name === 'string' && pr.tool_name) return pr.tool_name
  }
  // Fall back to extracting from title (e.g. "Tool: send_email")
  if (item.title.startsWith('Tool: ')) return item.title.slice('Tool: '.length)
  return item.title || 'unknown_tool'
}

function humaniseToolName(toolName: string): string {
  if (!toolName) return 'Unknown Tool'
  return toolName
    .split(/[_.]/g)
    .map((part) => (part.length === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1)))
    .join(' ')
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v)
}

/**
 * Return the first argument that is a non-empty (trimmed) string, else null.
 * Used to pick a value from primary/fallback payload locations.
 */
function firstNonEmptyString(...values: unknown[]): string | null {
  for (const v of values) {
    if (typeof v === 'string' && v.trim().length > 0) return v.trim()
  }
  return null
}

/**
 * Return the first argument that is a finite number, else null. Coerces
 * numeric strings (e.g. a rating serialised as "4") so a value surviving a
 * JSON round-trip as a string still resolves.
 */
function firstNumber(...values: unknown[]): number | null {
  for (const v of values) {
    if (typeof v === 'number' && Number.isFinite(v)) return v
    if (typeof v === 'string' && v.trim() !== '') {
      const n = Number(v)
      if (Number.isFinite(n)) return n
    }
  }
  return null
}

// ---------------------------------------------------------------------------
// Original-email extraction (Task 4054d)
//
// For `send_email` approvals, `details.trigger_payload` is the inbound
// Interaction (the `interaction.received` event payload — see
// `packages/go/interactions/interaction.go`). We lift the fields the
// `original_email` renderer needs: sender (`email.from`, plus `from_name`
// when a payload variant carries one), subject (`email.subject`), body
// (`html_body` with `plain_text_body` fallback), and attachments.
// ---------------------------------------------------------------------------

/**
 * Normalised attachment shape consumed by the `original_email` renderer.
 * Mirrors the inbox-manager `MessageAttachment` shape so the shared
 * `AttachmentChip`/`AttachmentPreviewModal` components can render it.
 */
export interface OriginalEmailAttachment {
  id: string
  filename: string
  content_type: string
  size: number
  url?: string
}

/** Data payload for the `original_email` card section. */
export interface OriginalEmailData {
  from?: string
  from_name?: string
  subject?: string
  html_body?: string
  plain_text_body?: string
  attachments?: OriginalEmailAttachment[]
}

/**
 * Extract the original inbound email from a `send_email` trigger payload.
 *
 * Returns null when no usable body is present (neither `html_body` nor
 * `plain_text_body`) — the caller then omits the section entirely rather
 * than rendering an empty card (graceful absence, per the task spec).
 *
 * Attachment tolerance: the Go Interaction model stores `attachments` as
 * asset-ID strings (`ast_xxx`), but hydrated payload variants may carry
 * object shapes (filename/content_type/size/url). Both are normalised;
 * string-only entries render as chips without preview/download (no URL).
 */
export function extractOriginalEmail(
  tp: Record<string, unknown>,
): OriginalEmailData | null {
  const htmlBody =
    typeof tp.html_body === 'string' && tp.html_body.trim().length > 0
      ? tp.html_body
      : null
  const plainTextBody =
    typeof tp.plain_text_body === 'string' && tp.plain_text_body.trim().length > 0
      ? tp.plain_text_body.trim()
      : null

  // No usable body — omit the section (sender/subject alone aren't enough
  // context to judge the reply against).
  if (!htmlBody && !plainTextBody) return null

  const out: OriginalEmailData = {}

  const email = isPlainObject(tp.email) ? (tp.email as Record<string, unknown>) : null
  if (email) {
    if (typeof email.from === 'string' && email.from.trim().length > 0) {
      out.from = email.from.trim()
    }
    // `from_name` isn't on the canonical EmailDetails struct today but some
    // payload variants carry it — surface it when present.
    if (typeof email.from_name === 'string' && email.from_name.trim().length > 0) {
      out.from_name = email.from_name.trim()
    }
    if (typeof email.subject === 'string' && email.subject.trim().length > 0) {
      out.subject = email.subject.trim()
    }
  }

  if (htmlBody) out.html_body = htmlBody
  if (plainTextBody) out.plain_text_body = plainTextBody

  const attachments = normaliseAttachments(tp.attachments)
  if (attachments.length > 0) out.attachments = attachments

  return out
}

function normaliseAttachments(value: unknown): OriginalEmailAttachment[] {
  if (!Array.isArray(value)) return []
  const out: OriginalEmailAttachment[] = []
  for (const [idx, entry] of value.entries()) {
    if (typeof entry === 'string') {
      if (entry.trim().length === 0) continue
      // Asset-ID-only reference: render a chip with the ID as the label.
      // Negative size renders as "—" via the shared formatSize helper.
      out.push({
        id: entry,
        filename: entry,
        content_type: '',
        size: -1,
      })
      continue
    }
    if (!isPlainObject(entry)) continue
    const filename =
      typeof entry.filename === 'string' && entry.filename.trim().length > 0
        ? entry.filename
        : typeof entry.id === 'string' && entry.id.trim().length > 0
          ? entry.id
          : null
    if (!filename) continue
    out.push({
      id:
        typeof entry.id === 'string' && entry.id.trim().length > 0
          ? entry.id
          : `${filename}-${idx}`,
      filename,
      content_type: typeof entry.content_type === 'string' ? entry.content_type : '',
      size:
        typeof entry.size === 'number' && Number.isFinite(entry.size) && entry.size >= 0
          ? entry.size
          : -1,
      ...(typeof entry.url === 'string' && entry.url.length > 0 ? { url: entry.url } : {}),
    })
  }
  return out
}

// ---------------------------------------------------------------------------
// Reviewer-segmentation extraction (Task 3702)
//
// `frustration_segment` and `engagement_signals` may arrive at Stream through
// several paths depending on where the backend lifts them onto the approval
// payload. These helpers tolerate every shape we currently observe and
// return null when the value is absent or malformed. Callers can union the
// two results — together they're enough to drive the section's render even
// when only partial data is available.
// ---------------------------------------------------------------------------

const FRUSTRATION_SEGMENT_VALUES = new Set([
  'wrong_fit',
  'tenure_with_specific_problem',
  'business_model_objection',
  'unable_to_classify',
])

/**
 * Look for the `frustration_segment` field on the approval payload.
 *
 * Checked locations (most-structured first):
 *   1. `details.proposed_resolution.frustration_segment`
 *   2. `details.frustration_segment` (top-level lift, reserved)
 *   3. step_executions: the support_manager step's parsed output
 *      (`step.output.frustration_segment`) when a backend post-process
 *      surfaces it.
 *
 * Returns the literal enum string when found and recognised, otherwise null.
 * Unknown values are intentionally rejected so a stray label can't pollute
 * the UI — better to omit the line than render `frustration_segment: garbage`.
 */
function extractFrustrationSegment(details: Record<string, unknown>): string | null {
  // 1) proposed_resolution.frustration_segment
  if (isPlainObject(details.proposed_resolution)) {
    const pr = details.proposed_resolution as Record<string, unknown>
    if (typeof pr.frustration_segment === 'string' && FRUSTRATION_SEGMENT_VALUES.has(pr.frustration_segment)) {
      return pr.frustration_segment
    }
  }
  // 2) top-level details.frustration_segment
  if (typeof details.frustration_segment === 'string' && FRUSTRATION_SEGMENT_VALUES.has(details.frustration_segment)) {
    return details.frustration_segment
  }
  // 3) step_executions output (any step) — defensive scan; the backend may
  //    surface it on a downstream unpack step in future.
  if (Array.isArray(details.step_executions)) {
    for (const step of details.step_executions) {
      if (!isPlainObject(step)) continue
      const output = step.output
      if (!isPlainObject(output)) continue
      const v = output.frustration_segment
      if (typeof v === 'string' && FRUSTRATION_SEGMENT_VALUES.has(v)) {
        return v
      }
    }
  }
  return null
}

/**
 * The shape of `EngagementSignals` from content_classifier's v3.3.0 schema.
 * Mirrors the Pydantic model in
 * `services/agents/backend/internal/core/agents/support/content_classifier/schemas/output.py`.
 *
 * All fields optional — partial extraction is acceptable. Strings/numbers/
 * booleans only; nested structures are not expected.
 */
interface EngagementSignals {
  tenure_days?: number
  streak_length?: number
  commitment_language?: boolean
  verbatim_quote?: string
}

/**
 * Look for the `engagement_signals` payload on the approval.
 *
 * Checked locations (most-structured first):
 *   1. `details.proposed_resolution.engagement_signals`
 *   2. `details.engagement_signals` (top-level lift, reserved)
 *   3. `details.trigger_payload.classification.engagement_signals` — the
 *      Interaction.Classification block as persisted by
 *      `step_persist_classification`. Present when the trigger payload
 *      reflects the post-classification interaction state.
 *   4. step_executions: scans every step's `output.engagement_signals`,
 *      catching the canonical `step_unpack_classifier` output as well as
 *      any downstream step that re-exposes the field.
 *
 * Returns a normalised `EngagementSignals` object (only fields with valid
 * types kept) or null when nothing usable was found.
 */
function extractEngagementSignals(details: Record<string, unknown>): EngagementSignals | null {
  const candidates: unknown[] = []
  if (isPlainObject(details.proposed_resolution)) {
    candidates.push((details.proposed_resolution as Record<string, unknown>).engagement_signals)
  }
  candidates.push(details.engagement_signals)
  if (isPlainObject(details.trigger_payload)) {
    const tp = details.trigger_payload as Record<string, unknown>
    if (isPlainObject(tp.classification)) {
      candidates.push((tp.classification as Record<string, unknown>).engagement_signals)
    }
  }
  if (Array.isArray(details.step_executions)) {
    for (const step of details.step_executions) {
      if (!isPlainObject(step)) continue
      const output = step.output
      if (!isPlainObject(output)) continue
      candidates.push(output.engagement_signals)
    }
  }

  for (const c of candidates) {
    const normalised = normaliseEngagementSignals(c)
    if (normalised) return normalised
  }
  return null
}

function normaliseEngagementSignals(value: unknown): EngagementSignals | null {
  if (!isPlainObject(value)) return null
  const out: EngagementSignals = {}
  if (typeof value.tenure_days === 'number' && Number.isFinite(value.tenure_days) && value.tenure_days >= 0) {
    out.tenure_days = value.tenure_days
  }
  if (typeof value.streak_length === 'number' && Number.isFinite(value.streak_length) && value.streak_length >= 0) {
    out.streak_length = value.streak_length
  }
  if (typeof value.commitment_language === 'boolean') {
    out.commitment_language = value.commitment_language
  }
  if (typeof value.verbatim_quote === 'string' && value.verbatim_quote.trim().length > 0) {
    out.verbatim_quote = value.verbatim_quote
  }
  return Object.keys(out).length > 0 ? out : null
}
